import { jsx as _jsx } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useFilmContext } from '../../../context/film/hooks/use-film-context';
import { useFilmTrailerUrl } from '../../../hooks/use-film-trailer-url';
import { VideoPlayer } from '../../../subcomponents/video-player/video-player';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { defaultFilmTrailerProps, filmTrailerPropsValidator } from './types/props';
const b = bemClassBuilder('v-film-trailer');
const FilmTrailerBase = (props, ref) => {
    const { displayText, assets, events, config } = props;
    const { failedToLoadTrailerTitle, failedToLoadTrailerRetryButtonLabel } = displayText;
    const { failedToLoadTrailerIconName } = assets;
    const videoPlayerRef = useRef(null);
    const { film } = useFilmContext();
    const [videoPlayerErrorRetryCount, setVideoPlayerRetryCount] = useState(0);
    const trailerUrl = useFilmTrailerUrl(film, undefined, events.onTrailerFailedToLoad);
    const incrementVideoPlayerRetryCount = () => {
        setVideoPlayerRetryCount(videoPlayerErrorRetryCount + 1);
    };
    const play = useCallback(() => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.play(); }, []);
    const pause = useCallback(() => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.pause(); }, []);
    const load = useCallback(() => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.load(); }, []);
    const setTime = useCallback((time) => { var _a; return (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.setTime(time); }, []);
    useImperativeHandle(ref, () => {
        return {
            play,
            pause,
            setTime,
            load
        };
    });
    if (!film || !trailerUrl)
        return null;
    return (_jsx(VideoPlayer, { src: trailerUrl, className: b.block(), displayText: {
            failedToLoadTitle: failedToLoadTrailerTitle,
            failedToLoadRetryButtonLabel: failedToLoadTrailerRetryButtonLabel
        }, assets: {
            failedToLoadIconName: failedToLoadTrailerIconName
        }, events: {
            onRetryButtonClicked: incrementVideoPlayerRetryCount,
            onVideoLoaded: events.onTrailerLoaded,
            onVideoFailedToLoad: events.onTrailerFailedToLoad,
            onVideoError: events.onTrailerError
        }, ref: videoPlayerRef, controls: config.showControls || undefined, autoPlay: config.autoPlay, muted: config.muted, loop: config.loop, onClick: events.onTrailerClick, onPlay: events.onTrailerPlay, onPause: events.onTrailerPause, onEnded: events.onTrailerEnded }, videoPlayerErrorRetryCount));
};
export const FilmTrailer = observer(forwardRef(FilmTrailerBase));
FilmTrailer.displayName = 'FilmTrailer';
/**
 * The **Film Trailer** component renders the trailer for a film.
 */
export const FilmTrailerComponent = withComponentWrapper(FilmTrailer, defaultFilmTrailerProps, filmTrailerPropsValidator);
